/**
 * MarkeeMix Scale Toolkit — bulk 256K-context workflows.
 * Route: /tools/scale-toolkit
 *
 * Tabs:
 *   1. Bulk Meta Generator     — CSV of pages → 500 SEO title + description pairs
 *   2. Programmatic SEO Pages  — Template + CSV → 1000 landing page drafts
 *   3. Regional Newsletter     — One campaign → 10 Indic language variants + voice notes
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Table2, Layout, Mail, Loader2, Copy, Download, Upload, Sparkles, FileSpreadsheet,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/scale`;

// Poll a bulk job (bulk-meta / programmatic-seo) until it finishes or times out.
// onProgress receives the latest job payload after every poll so the UI can
// stream partial results as they arrive.
async function pollJob(jobId, onProgress, { intervalMs = 3500, maxMs = 600000 } = {}) {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const { data } = await axios.get(`${API}/jobs/${jobId}`, { timeout: 15000 });
    onProgress?.(data);
    if (data.status === "completed" || data.status === "failed") return data;
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  throw new Error("Polling timed out — job may still be running on the server.");
}

const ALL_LANGS = [
  { code: "hi-IN", label: "Hindi" }, { code: "ta-IN", label: "Tamil" },
  { code: "te-IN", label: "Telugu" }, { code: "bn-IN", label: "Bengali" },
  { code: "mr-IN", label: "Marathi" }, { code: "gu-IN", label: "Gujarati" },
  { code: "kn-IN", label: "Kannada" }, { code: "ml-IN", label: "Malayalam" },
  { code: "pa-IN", label: "Punjabi" }, { code: "od-IN", label: "Odia" },
];

const TABS = [
  { id: "meta", label: "Bulk Meta Generator", Icon: Table2, desc: "CSV → 500 SEO titles + descriptions" },
  { id: "pseo", label: "Programmatic SEO Pages", Icon: Layout, desc: "Template + CSV → 1000 landing pages" },
  { id: "newsletter", label: "Regional Newsletter", Icon: Mail, desc: "One campaign → 10 languages + voice notes" },
];

function copy(t) { navigator.clipboard.writeText(t); toast.success("Copied!"); }

function downloadCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csvStr = [
    headers.join(","),
    ...rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob([csvStr], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const parseLine = (l) => {
    const cells = [];
    let cur = "", inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
      else { cur += ch; }
    }
    cells.push(cur);
    return cells.map(c => c.trim());
  };
  const headers = parseLine(lines[0]);
  return lines.slice(1).map(l => {
    const cells = parseLine(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] || ""; });
    return obj;
  });
}

// ─── 1. Bulk Meta Generator ─────────────────────────────────────
function BulkMetaPanel() {
  const [csvText, setCsvText] = useState("url,topic,hint\n/blog/coffee-guide,coffee brewing methods,for home baristas\n/pricing,SaaS pricing plans,MarkeeMix AI workspace");
  const [voice, setVoice] = useState("clear, benefit-first, no fluff");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const rows = parseCsv(csvText);

  const run = async () => {
    if (rows.length === 0) { toast.error("Add at least 1 row"); return; }
    if (rows.length > 500) { toast.error("Max 500 rows per run"); return; }
    setBusy(true); setResult(null);
    try {
      const { data: kick } = await axios.post(
        `${API}/bulk-meta`,
        { rows, brand_voice: voice },
        { timeout: 30000 },
      );
      if (!kick?.job_id) throw new Error("Server didn't return a job id");
      const final = await pollJob(kick.job_id, (snap) => setResult(snap));
      if (final.status === "failed") toast.error(final.error || "Bulk generation failed");
      else toast.success(`Generated ${final.count || 0} metas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Bulk generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <label className="text-xs font-semibold text-neutral-600 block">
        Brand voice
        <Input value={voice} onChange={(e) => setVoice(e.target.value)} className="mt-1" data-testid="meta-voice" />
      </label>
      <label className="text-xs font-semibold text-neutral-600 block">
        CSV rows (columns: <code>url,topic,hint</code>)
        <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={8}
          className="mt-1 font-mono text-xs" data-testid="meta-csv" />
      </label>
      <p className="text-xs text-neutral-500">{rows.length} row{rows.length !== 1 && "s"} · max 500</p>
      <Button onClick={run} disabled={busy || rows.length === 0} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="meta-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing metas… {result?.completed ? `(${result.completed}/${result.requested})` : ""}</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate metas</>}
      </Button>

      {result?.metas?.length > 0 && (
        <div className="space-y-3" data-testid="meta-result">
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent">{result.count} / {result.requested} generated</Badge>
            <Button size="sm" variant="outline" onClick={() => downloadCsv(result.metas, "bulk-metas.csv")}>
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="p-2 font-semibold">URL</th>
                  <th className="p-2 font-semibold">Title</th>
                  <th className="p-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                {result.metas.slice(0, 50).map((m, i) => (
                  <tr key={i} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="p-2 font-mono text-[10px] text-neutral-500 max-w-[180px] truncate">{m.url}</td>
                    <td className="p-2 font-semibold text-[#111111]">{m.title || <span className="text-red-500">(error)</span>}</td>
                    <td className="p-2 text-neutral-700 max-w-[400px]">{m.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.metas.length > 50 && <p className="text-xs text-neutral-500">Showing first 50 · Export CSV for all {result.count}</p>}
        </div>
      )}
    </div>
  );
}

// ─── 2. Programmatic SEO Pages ──────────────────────────────────
function PSeoPanel() {
  const [pattern, setPattern] = useState("/best-{tool}-for-{industry}");
  const [csvText, setCsvText] = useState("tool,industry\ncrm,restaurants\ncrm,real estate\nemail,e-commerce\naccounting,doctors\nkeyword research,agencies");
  const [audience, setAudience] = useState("Indian SMB buyers");
  const [tone, setTone] = useState("expert, practical");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const rows = parseCsv(csvText);

  const run = async () => {
    if (!pattern.trim() || rows.length === 0) { toast.error("Template + CSV rows required"); return; }
    setBusy(true); setResult(null); setSelectedIdx(0);
    try {
      const { data: kick } = await axios.post(
        `${API}/programmatic-seo`,
        { template_pattern: pattern, rows, audience, tone },
        { timeout: 30000 },
      );
      if (!kick?.job_id) throw new Error("Server didn't return a job id");
      const final = await pollJob(kick.job_id, (snap) => setResult(snap));
      if (final.status === "failed") toast.error(final.error || "pSEO generation failed");
      else toast.success(`Drafted ${final.count} pages`);
    } catch (e) {
      const msg = e?.code === "ECONNABORTED"
        ? "Kick-off took too long. Try 5-10 rows at a time."
        : (e?.response?.data?.detail || e?.message || "pSEO generation failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const page = result?.pages?.[selectedIdx];

  return (
    <div className="space-y-4">
      <label className="text-xs font-semibold text-neutral-600 block">
        Slug template
        <Input value={pattern} onChange={(e) => setPattern(e.target.value)}
          placeholder="/best-{tool}-for-{industry}" className="mt-1 font-mono text-xs" data-testid="pseo-pattern" />
      </label>
      <label className="text-xs font-semibold text-neutral-600 block">
        Variable rows (CSV — columns must match the <code>{"{var}"}</code> names in the pattern)
        <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={6}
          className="mt-1 font-mono text-xs" data-testid="pseo-csv" />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">Audience
          <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1" data-testid="pseo-audience" />
        </label>
        <label className="text-xs font-semibold text-neutral-600">Tone
          <Input value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1" data-testid="pseo-tone" />
        </label>
      </div>
      <p className="text-xs text-neutral-500">{rows.length} pages · max 1000</p>
      <Button onClick={run} disabled={busy || rows.length === 0} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="pseo-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing pages… {result?.completed ? `(${result.completed}/${result.requested})` : ""}</> : <><Layout className="w-4 h-4 mr-2" /> Generate landing pages</>}
      </Button>

      {result?.pages?.length > 0 && (
        <div data-testid="pseo-result" className="space-y-3">
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent">{result.count} / {result.requested} generated</Badge>
            <Button size="sm" variant="outline" onClick={() => downloadCsv(
              result.pages.map(p => ({ slug: p.slug, h1: p.h1, meta_title: p.meta_title, meta_description: p.meta_description })),
              "pseo-pages.csv")}>
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
          <div className="grid sm:grid-cols-[220px_1fr] gap-4 border border-neutral-200 rounded-lg overflow-hidden">
            <div className="border-r border-neutral-200 bg-neutral-50 max-h-[500px] overflow-y-auto">
              {result.pages.map((p, i) => (
                <button key={i} onClick={() => setSelectedIdx(i)}
                  className={`block w-full text-left px-3 py-2 text-xs border-b border-neutral-100 hover:bg-white ${i === selectedIdx ? "bg-white font-semibold text-[#7C3AED]" : "text-neutral-700"}`}>
                  {p.slug || `page-${i}`}
                </button>
              ))}
            </div>
            <div className="p-5 max-h-[500px] overflow-y-auto">
              {page ? (
                <>
                  <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">{page.slug}</p>
                  <h2 className="font-heading text-2xl font-semibold text-[#111111] mt-1">{page.h1}</h2>
                  {page.hero_subtitle && <p className="text-sm text-neutral-600 mt-2">{page.hero_subtitle}</p>}
                  {(page.sections || []).map((s, i) => (
                    <div key={i} className="mt-4">
                      <h3 className="font-heading text-base font-semibold text-[#111111]">{s.heading}</h3>
                      <p className="text-sm text-neutral-700 mt-1 leading-relaxed">{s.body}</p>
                    </div>
                  ))}
                  {page.faq?.length > 0 && (
                    <div className="mt-6 border-t border-neutral-200 pt-4">
                      <p className="text-xs font-bold uppercase text-neutral-500 tracking-wider mb-3">FAQ</p>
                      {page.faq.map((f, i) => (
                        <div key={i} className="mb-3">
                          <p className="text-sm font-semibold text-[#111111]">Q: {f.q}</p>
                          <p className="text-sm text-neutral-700 mt-0.5">A: {f.a}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-6 pt-4 border-t border-neutral-200 text-xs text-neutral-500">
                    <p><b>Meta title:</b> {page.meta_title}</p>
                    <p><b>Meta description:</b> {page.meta_description}</p>
                  </div>
                </>
              ) : <p className="text-sm text-neutral-500">Pick a page from the left.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 3. Regional Newsletter Studio ──────────────────────────────
function NewsletterPanel() {
  const [subject, setSubject] = useState("Diwali Special — 30% off all AI plans");
  const [body, setBody] = useState("Hey there!\n\nThis Diwali, unlock every MarkeeMix AI tool at 30% off — including our new Indic AI stack (22 language STT + 40+ Indian voices + regional ads).\n\nOffer ends this Sunday.");
  const [cta, setCta] = useState("Grab 30% off");
  const [ctaUrl, setCtaUrl] = useState("https://markeemix.com/pricing");
  const [langs, setLangs] = useState(["hi-IN", "ta-IN", "te-IN", "mr-IN", "bn-IN"]);
  const [includeVoice, setIncludeVoice] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (code) => setLangs(langs.includes(code) ? langs.filter(l => l !== code) : [...langs, code]);

  const run = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/newsletter`, {
        subject, body, cta_label: cta, cta_url: ctaUrl, languages: langs,
        include_voice: includeVoice, include_whatsapp: true,
      }, { timeout: 240000 });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Newsletter generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <label className="text-xs font-semibold text-neutral-600 block">
        Subject
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="nl-subject" />
      </label>
      <label className="text-xs font-semibold text-neutral-600 block">
        Body
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="mt-1" data-testid="nl-body" />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">CTA text
          <Input value={cta} onChange={(e) => setCta(e.target.value)} className="mt-1" data-testid="nl-cta" />
        </label>
        <label className="text-xs font-semibold text-neutral-600">CTA URL
          <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className="mt-1" data-testid="nl-cta-url" />
        </label>
      </div>
      <div>
        <div className="text-xs font-semibold text-neutral-600 mb-2">Publish in ({langs.length} languages)</div>
        <div className="flex flex-wrap gap-2">
          {ALL_LANGS.map(l => {
            const on = langs.includes(l.code);
            return (
              <button key={l.code} type="button" onClick={() => toggle(l.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  on ? "bg-rose-500 text-white border-rose-500" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                }`} data-testid={`nl-lang-${l.code}`}>
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-neutral-700">
        <input type="checkbox" checked={includeVoice} onChange={(e) => setIncludeVoice(e.target.checked)} data-testid="nl-voice-toggle" />
        Also generate a voice note per language (WhatsApp companion)
      </label>
      <Button onClick={run} disabled={busy} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="nl-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Localising campaign…</> : <><Mail className="w-4 h-4 mr-2" /> Localise for {langs.length} languages</>}
      </Button>

      {result?.campaigns?.length > 0 && (
        <div className="space-y-3" data-testid="nl-result">
          {result.campaigns.map((c, i) => (
            <Card key={i} className="p-5 border-neutral-200">
              <div className="flex items-center justify-between mb-3">
                <Badge className="bg-rose-100 text-rose-800 border-transparent">{c.language_label}</Badge>
                <Button size="sm" variant="outline" onClick={() => copy(`${c.subject}\n\n${c.body}\n\n${c.cta_label}: ${c.cta_url}`)}>
                  <Copy className="w-3 h-3 mr-1" /> Copy email
                </Button>
              </div>
              <p className="font-heading text-lg font-semibold text-[#111111]">{c.subject}</p>
              <p className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap leading-relaxed">{c.body}</p>
              <a href={c.cta_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 px-4 py-2 bg-[#A855F7] text-white text-sm font-semibold rounded-md">{c.cta_label} →</a>
              {c.whatsapp_text && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider mb-1">WhatsApp companion</p>
                  <p className="text-xs whitespace-pre-wrap text-emerald-900 font-mono">{c.whatsapp_text}</p>
                  <Button size="sm" variant="outline" onClick={() => copy(c.whatsapp_text)} className="mt-2">
                    <Copy className="w-3 h-3 mr-1" /> Copy WhatsApp
                  </Button>
                </div>
              )}
              {c.voice_base64 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase font-bold text-neutral-500 tracking-wider mb-1">Voice note</p>
                  <audio controls src={`data:audio/wav;base64,${c.voice_base64}`} className="w-full h-9" />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ScaleToolkitPage() {
  const [tab, setTab] = useState("meta");
  const Active = { meta: BulkMetaPanel, pseo: PSeoPanel, newsletter: NewsletterPanel }[tab];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-indigo-100 text-indigo-800 border-transparent">📊 Bulk workflows</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent">256K context</Badge>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            Scale Toolkit
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl text-base">
            Three bulk workflows that turn a single spreadsheet or campaign into hundreds of
            output artifacts — perfect for programmatic SEO, agency clients and multi-language
            newsletters.
          </p>
        </div>
      </section>

      <div className="border-b border-neutral-200 bg-white sticky top-16 z-10">
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-4 border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active ? "border-[#A855F7] text-[#7C3AED]" : "border-transparent text-neutral-500 hover:text-neutral-800"
                }`} data-testid={`scale-tab-${t.id}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        <div className="mb-4 text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
          <FileSpreadsheet className="w-3 h-3" /> {TABS.find(t => t.id === tab)?.desc}
        </div>
        <Card className="p-6 border-neutral-200 bg-white">
          <Active />
        </Card>

        <div className="mt-8 text-center text-xs text-neutral-500">
          Part of the <span className="text-[#7C3AED] font-semibold">MarkeeMix AI</span> stack
        </div>
      </main>

      <PremiumFooter />
    </div>
  );
}
